import { UnitCost } from './units'

export interface BuildingType {
  id: string
  name: string
  description: string
  hp: number
  size: number  // tiles (buildings are square)
  cost: UnitCost
  buildTime: number  // seconds
  provides?: {
    populationCap?: number
  }
  generates?: {
    compute?: number
    data?: number
    energy?: number
  }
  canTrain?: string[]  // unit type IDs
  canResearch?: boolean
  attack?: number
  attackSpeed?: number
  range?: number
  isBase?: boolean
  isWonder?: boolean
  requiresTerrain?: number  // terrain ID
}

export const BUILDING_TYPES: Record<string, BuildingType> = {
  DATACENTER: {
    id: 'DATACENTER',
    name: 'Data Center',
    description: 'Main base building. Trains worker units and provides population capacity.',
    hp: 600,
    size: 3,
    cost: { compute: 300 },
    buildTime: 40,
    provides: { populationCap: 6 },
    canTrain: ['WORKER'],
    isBase: true
  },
  COMPUTE_FARM: {
    id: 'COMPUTE_FARM',
    name: 'Compute Farm',
    description: 'Generates compute resources over time.',
    hp: 250,
    size: 2,
    cost: { compute: 120 },
    buildTime: 25,
    generates: { compute: 12 }
  },
  DATA_MINE: {
    id: 'DATA_MINE',
    name: 'Data Mine',
    description: 'Extracts data from forest tiles. Must be built on forest.',
    hp: 180,
    size: 2,
    cost: { compute: 90 },
    buildTime: 20,
    generates: { data: 10 },
    requiresTerrain: 3  // FOREST
  },
  POWER_PLANT: {
    id: 'POWER_PLANT',
    name: 'Power Plant',
    description: 'Generates energy resources. Essential for advanced units.',
    hp: 280,
    size: 2,
    cost: { compute: 130, data: 60 },
    buildTime: 30,
    generates: { energy: 6 }
  },
  BARRACKS: {
    id: 'BARRACKS',
    name: 'Training Cluster',
    description: 'Trains military units. Scouts and Warriors.',
    hp: 350,
    size: 2,
    cost: { compute: 180, data: 60 },
    buildTime: 30,
    canTrain: ['SCOUT', 'WARRIOR']
  },
  RESEARCH_LAB: {
    id: 'RESEARCH_LAB',
    name: 'Research Lab',
    description: 'Trains advanced units and enables research.',
    hp: 300,
    size: 2,
    cost: { compute: 220, data: 180 },
    buildTime: 40,
    canTrain: ['ARCHER', 'REASONER'],
    canResearch: true
  },
  FACTORY: {
    id: 'FACTORY',
    name: 'Hero Factory',
    description: 'Can train legendary hero units.',
    hp: 500,
    size: 3,
    cost: { compute: 400, data: 300, energy: 200 },
    buildTime: 60,
    canTrain: ['HERO_OPUS', 'HERO_SONNET', 'HERO_HAIKU']
  },
  TOWER: {
    id: 'TOWER',
    name: 'Watchtower',
    description: 'Defensive structure with ranged attack.',
    hp: 450,
    size: 1,
    cost: { compute: 120, energy: 60 },
    buildTime: 25,
    attack: 12,
    attackSpeed: 0.8,
    range: 7
  },
  WALL: {
    id: 'WALL',
    name: 'Firewall',
    description: 'Defensive barrier with high HP. Blocks enemy movement.',
    hp: 800,
    size: 1,
    cost: { compute: 50 },
    buildTime: 10
  },
  WONDER: {
    id: 'WONDER',
    name: 'Singularity Core',
    description: 'Victory condition. Complete this to achieve technological transcendence.',
    hp: 3000,
    size: 4,
    cost: { compute: 1500, data: 1500, energy: 800 },
    buildTime: 180,
    isWonder: true
  }
}

export function getBuildingType(id: string): BuildingType {
  return BUILDING_TYPES[id]
}

export function getBuildingCost(id: string, factionCostMod: number = 1): UnitCost {
  const building = BUILDING_TYPES[id]
  if (!building) return {}

  const cost: UnitCost = {}
  if (building.cost.compute) cost.compute = Math.floor(building.cost.compute * factionCostMod)
  if (building.cost.data) cost.data = Math.floor(building.cost.data * factionCostMod)
  if (building.cost.energy) cost.energy = Math.floor(building.cost.energy * factionCostMod)
  return cost
}
